import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Icon } from '../components/icons';
import { Badge, ErrorState, Field, LoadingRows, PageHeader } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api, ApiError } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatPhone } from '../lib/format';
import { qk, useSettings } from '../lib/queries';
import type { PanelConfig, TotpSetup } from '../lib/types';

type Tab = 'panel' | 'saatler' | 'alicilar' | 'guvenlik';

const TABS: { value: Tab; label: string }[] = [
  { value: 'panel', label: 'Panel' },
  { value: 'saatler', label: 'Çalışma saatleri' },
  { value: 'alicilar', label: 'Test alıcıları' },
  { value: 'guvenlik', label: 'Güvenlik (2FA)' }
];

const TIMEZONES = ['Europe/Istanbul', 'UTC', 'Europe/Berlin', 'Europe/London', 'America/New_York'];


/** S11 — Ayarlar: GET/PUT /api/settings (panel, çalışma saatleri, test alıcıları) + TOTP güvenlik. */
export default function SettingsPage() {
  const settings = useSettings();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [tab, setTab] = useState<Tab>('panel');
  const [draft, setDraft] = useState<PanelConfig | null>(null);
  const [recipient, setRecipient] = useState('');

  // Güvenlik / TOTP
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [regenMode, setRegenMode] = useState(false);

  useEffect(() => {
    if (settings.data && !draft) setDraft(settings.data);
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: (value: PanelConfig) => api.saveSettings(value),
    onSuccess: (saved) => {
      setDraft(saved);
      toast.push('Ayarlar kaydedildi.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.settings });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const totpSetupMutation = useMutation({
    mutationFn: () => api.totpSetup(),
    onSuccess: (value) => {
      setSetup(value);
      setRecoveryCodes(null);
      toast.push('TOTP sırrı üretildi. Authenticator uygulamasına ekleyin.', 'ok');
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const totpEnableMutation = useMutation({
    mutationFn: (code: string) => api.totpEnable(code),
    onSuccess: (codes) => {
      setRecoveryCodes(codes);
      setSetup(null);
      setTotpCode('');
      toast.push('2FA etkinleştirildi. Kurtarma kodlarını güvenli bir yere kaydedin.', 'ok');
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const regenMutation = useMutation({
    mutationFn: (code: string) => api.regenerateRecoveryCodes(code),
    onSuccess: (codes) => {
      setRecoveryCodes(codes);
      setRegenMode(false);
      setTotpCode('');
      toast.push('Kurtarma kodları yenilendi; diğer oturumlar kapatıldı.', 'ok');
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  async function copyCodes() {
    if (!recoveryCodes) return;
    await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.push('Kurtarma kodları kopyalandı.', 'ok');
  }

  function downloadCodes() {
    if (!recoveryCodes) return;
    const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kurtarma-kodlari.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  if (settings.isLoading) return <LoadingRows rows={8} columns={2} />;
  if (settings.isError) {
    return <ErrorState message={errorMessage(settings.error)} onRetry={() => void settings.refetch()} />;
  }

  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(settings.data);

  return (
    <div>
      <PageHeader
        title="Ayarlar"
        subtitle="Panel kimliği, çalışma saatleri, test alıcıları ve hesap güvenliği. Gizli değerler panelde gösterilmez."
        actions={
          draft ? (
            <>
              <button type="button" className="btn btn-sm" onClick={() => setDraft(settings.data ?? null)} disabled={!dirty}>
                Geri al
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => save.mutate(draft)}
                disabled={!dirty || save.isPending}
              >
                <Icon name="check" size={14} />
                Kaydet
              </button>
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 min-[1000px]:grid-cols-[200px_minmax(0,1fr)]">
        <nav className="border-b border-line bg-surface px-2 py-2 min-[1000px]:min-h-[520px] min-[1000px]:border-b-0 min-[1000px]:border-r" aria-label="Ayar bölümleri">
          <ul>
            {TABS.map((entry) => (
              <li key={entry.value}>
                <button
                  type="button"
                  aria-pressed={tab === entry.value}
                  className={
                    'flex h-8 w-full items-center rounded-[4px] px-2 text-left text-[13px] ' +
                    (tab === entry.value ? 'bg-signal-soft font-medium text-signal' : 'text-ink-2 hover:bg-surface-2')
                  }
                  onClick={() => setTab(entry.value)}
                >
                  {entry.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="max-w-[760px] p-6">


          {tab === 'panel' ? (
            <section className="panel">
              <div className="panel-head">
                <h2 className="text-[13px]">Panel bilgileri</h2>
              </div>
              <div className="panel-body space-y-3">
                <Field label="Panel adı" htmlFor="panel-name" hint="Kenar çubuğunda ve e-posta imzasında görünür.">
                  <input
                    id="panel-name"
                    className="input"
                    value={draft?.panelName ?? ''}
                    onChange={(event) => draft && setDraft({ ...draft, panelName: event.target.value })}
                   
                  />
                </Field>
                <Field label="Destek e-postası" htmlFor="support-email" hint="Müşteriye giden e-postalarda yanıt adresi.">
                  <input
                    id="support-email"
                    className="input mono"
                    type="email"
                    value={draft?.supportEmail ?? ''}
                    onChange={(event) => draft && setDraft({ ...draft, supportEmail: event.target.value })}
                   
                  />
                </Field>
                <Field label="Saat dilimi" htmlFor="timezone" hint="SLA, sessiz saatler ve raporlar bu dilime göre hesaplanır.">
                  <input
                    id="timezone"
                    className="input mono"
                    list="timezone-list"
                    value={draft?.timezone ?? ''}
                    onChange={(event) => draft && setDraft({ ...draft, timezone: event.target.value })}
                   
                  />
                  <datalist id="timezone-list">
                    {TIMEZONES.map((zone) => (
                      <option key={zone} value={zone} />
                    ))}
                  </datalist>
                </Field>
              </div>
            </section>
          ) : null}

          {tab === 'saatler' ? (
            <section className="panel">
              <div className="panel-head">
                <h2 className="text-[13px]">Çalışma saatleri</h2>
                <span className="text-[11px] text-muted">Bu aralık dışında yanıtlar kuyruğa alınır</span>
              </div>
              <div className="panel-body space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Başlangıç" htmlFor="hours-start">
                    <input
                      id="hours-start"
                      className="input mono"
                      type="time"
                      value={draft?.businessHoursStart ?? '09:00'}
                      onChange={(event) => draft && setDraft({ ...draft, businessHoursStart: event.target.value })}
                    />
                  </Field>
                  <Field label="Bitiş" htmlFor="hours-end">
                    <input
                      id="hours-end"
                      className="input mono"
                      type="time"
                      value={draft?.businessHoursEnd ?? '18:00'}
                      onChange={(event) => draft && setDraft({ ...draft, businessHoursEnd: event.target.value })}
                    />
                  </Field>
                </div>


              </div>
            </section>
          ) : null}

          {tab === 'alicilar' ? (
            <section className="panel">
              <div className="panel-head">
                <h2 className="text-[13px]">Test alıcıları</h2>
                <span className="text-[11px] text-muted">Test modunda yalnız bu numaralara gönderim yapılır</span>
              </div>
              <div className="panel-body space-y-3">
                <ul className="divide-y divide-line">
                  {(draft?.testRecipients ?? []).map((phone) => (
                    <li key={phone} className="flex items-center gap-2 py-2">
                      <Icon name="phone" size={14} className="text-muted" />
                      <span className="mono text-[13px]">{formatPhone(phone)}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm ml-auto text-danger"
                        aria-label={formatPhone(phone) + ' numarasını çıkar'}
                       
                        onClick={() => draft && setDraft({ ...draft, testRecipients: draft.testRecipients.filter((item) => item !== phone) })}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </li>
                  ))}
                  {(draft?.testRecipients ?? []).length === 0 ? (
                    <li className="py-2 text-[12px] text-muted">Henüz test alıcısı yok.</li>
                  ) : null}
                </ul>

                <form
                  className="flex items-end gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!draft) return;
                    const value = recipient.trim();
                    if (value.length < 10) return;
                    if (draft.testRecipients.includes(value)) return;
                    setDraft({ ...draft, testRecipients: [...draft.testRecipients, value] });
                    setRecipient('');
                  }}
                >
                  <Field label="Numara ekle" htmlFor="test-recipient" hint="Örnek: +90 551 888 58 86">
                    <input
                      id="test-recipient"
                      className="input mono"
                      value={recipient}
                      onChange={(event) => setRecipient(event.target.value)}
                      placeholder="+90 5xx xxx xx xx"
                     
                    />
                  </Field>
                  <button type="submit" className="btn" disabled={recipient.trim().length < 10}>
                    <Icon name="plus" size={14} />
                    Ekle
                  </button>
                </form>
                <p className="text-[11px] leading-4 text-muted">
                  Değişiklikler “Kaydet” ile sunucuya yazılır. Test modu ekranındaki izin listesi ayrıdır (guard katmanı).
                </p>
              </div>
            </section>
          ) : null}

          {tab === 'guvenlik' ? (
            <div className="space-y-4">
              <section className="panel">
                <div className="panel-head">
                  <h2 className="text-[13px]">İki adımlı doğrulama (TOTP)</h2>
                  <Badge tone={user?.totpEnabled ? 'ok' : 'warn'}>{user?.totpEnabled ? '2FA etkin' : '2FA kapalı'}</Badge>
                </div>
                <div className="panel-body space-y-3">
                  {user?.totpEnabled ? (
                    <>
                      <p className="text-[12px] leading-5 text-ink-2">
                        Hesabınızda TOTP etkin. Girişte parola sonrası authenticator kodunuz istenir. Kurtarma
                        kodlarını kaybettiyseniz aşağıdan yenileyebilirsiniz; bu işlem diğer oturumları kapatır.
                      </p>
                      {regenMode ? (
                        <div className="flex items-end gap-2">
                          <Field label="Doğrulama kodu" htmlFor="regen-code" hint="Authenticator uygulamasındaki 6 haneli kod">
                            <input
                              id="regen-code"
                              className="input mono tracking-[0.2em]"
                              inputMode="numeric"
                              maxLength={6}
                              value={totpCode}
                              onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, ''))}
                            />
                          </Field>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={totpCode.length !== 6 || regenMutation.isPending}
                            onClick={() => regenMutation.mutate(totpCode)}
                          >
                            Yenile
                          </button>
                          <button type="button" className="btn" onClick={() => setRegenMode(false)}>
                            Vazgeç
                          </button>
                        </div>
                      ) : (
                        <button type="button" className="btn" onClick={() => setRegenMode(true)}>
                          <Icon name="refresh" size={14} />
                          Kurtarma kodlarını yenile
                        </button>
                      )}
                    </>
                  ) : setup ? (
                    <>
                      <div className="flex flex-wrap items-start gap-4">
                        <div className="rounded-[4px] border border-line bg-surface p-2">
                          <QRCodeSVG value={setup.otpauthUrl} size={168} level="M" bgColor="#ffffff" fgColor="#10161c" />
                        </div>
                        <div className="min-w-[240px] flex-1 space-y-2">
                          <p className="text-[12px] leading-5 text-ink-2">
                            Authenticator uygulamasında yeni hesap ekleyin ve QR'ı okutun. Kod okutamıyorsanız sırrı
                            elle girin.
                          </p>
                          <div className="mono rounded-[4px] border border-line bg-paper px-2 py-1 text-[12px]">{setup.secret}</div>
                          <div className="flex items-end gap-2">
                            <Field label="Doğrulama kodu" htmlFor="totp-code" hint="Uygulamadaki 6 haneli kod">
                              <input
                                id="totp-code"
                                className="input mono tracking-[0.2em]"
                                inputMode="numeric"
                                maxLength={6}
                                value={totpCode}
                                onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, ''))}
                              />
                            </Field>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={totpCode.length !== 6 || totpEnableMutation.isPending}
                              onClick={() => totpEnableMutation.mutate(totpCode)}
                            >
                              Etkinleştir
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-[12px] leading-5 text-ink-2">
                        Break-glass girişlerinde ikinci adım olarak TOTP kullanılır. Kurulumda QR gösterilir, kod
                        doğrulandığında 2FA etkinleşir ve 10 kurtarma kodu üretilir.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => totpSetupMutation.mutate()}
                        disabled={totpSetupMutation.isPending}
                      >
                        <Icon name="shield" size={14} />
                        {totpSetupMutation.isPending ? 'Hazırlanıyor…' : '2FA kurulumunu başlat'}
                      </button>
                    </>
                  )}
                </div>
              </section>

              {recoveryCodes ? (
                <section className="panel border-[#ead9b4]">
                  <div className="panel-head">
                    <h2 className="text-[13px]">Kurtarma kodları</h2>
                    <span className="text-[11px] text-muted">Yalnız bir kez gösterilir</span>
                  </div>
                  <div className="panel-body space-y-2">
                    <p className="text-[12px] text-ink-2">
                      Bu kodları güvenli bir yere kaydedin. Her kod tek kullanımlıktır; authenticator erişiminizi
                      kaybettiğinizde giriş için kullanılır.
                    </p>
                    <pre className="mono grid grid-cols-2 gap-1 rounded-[4px] border border-line bg-paper p-3 text-[12px]">
                      {recoveryCodes.map((code) => (
                        <span key={code}>{code}</span>
                      ))}
                    </pre>
                    <div className="flex gap-2">
                      <button type="button" className="btn btn-sm" onClick={() => void copyCodes()}>
                        <Icon name="file" size={14} />
                        Kopyala
                      </button>
                      <button type="button" className="btn btn-sm" onClick={downloadCodes}>
                        <Icon name="download" size={14} />
                        İndir
                      </button>
                      <button type="button" className="btn btn-sm ml-auto" onClick={() => setRecoveryCodes(null)}>
                        Kaydettim, kapat
                      </button>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}